/**
 * 版本检测和缓存清除工具
 */

const VERSION_KEY = 'APP_VERSION_INFO'
const CHECK_INTERVAL = 5 * 60 * 1000 // 5分钟检测一次

/**
 * 获取服务器版本信息
 */
export async function fetchServerVersion() {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      method: 'GET',
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
    return await response.json()
  } catch (error) {
    console.error('获取版本信息失败：', error)
    return null
  }
}

/**
 * 获取本地存储的版本信息
 */
export function getLocalVersion() {
  try {
    const versionStr = localStorage.getItem(VERSION_KEY)
    return versionStr ? JSON.parse(versionStr) : null
  } catch (error) {
    console.error('读取本地版本信息失败：', error)
    return null
  }
}

/**
 * 保存版本信息到本地
 */
export function saveLocalVersion(versionInfo) {
  try {
    localStorage.setItem(VERSION_KEY, JSON.stringify(versionInfo))
    return true
  } catch (error) {
    console.error('保存版本信息失败：', error)
    return false
  }
}

/**
 * 清除应用缓存
 */
export function clearAppCache() {
  try {
    // 需要保留的 localStorage key
    const keysToKeep = ['token', 'userInfo'] // 根据实际需要保留的 key 添加
    
    // 清除 localStorage（保留指定 key）
    const allKeys = Object.keys(localStorage)
    allKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        localStorage.removeItem(key)
      }
    })

    // 清除 sessionStorage
    sessionStorage.clear()

    // 清除 Service Worker 缓存
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (const registration of registrations) {
          registration.unregister()
        }
      })
    }

    // 清除 Cache API
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName)
        })
      })
    }

    console.log('✅ 缓存清除成功')
    return true
  } catch (error) {
    console.error('清除缓存失败：', error)
    return false
  }
}

/**
 * 检查是否需要更新
 * @returns {Promise<boolean>} 是否需要更新
 */
export async function checkVersionUpdate() {
  const serverVersion = await fetchServerVersion()
  if (!serverVersion) {
    return false
  }

  const localVersion = getLocalVersion()
  
  // 首次访问，保存版本信息
  if (!localVersion) {
    saveLocalVersion(serverVersion)
    return false
  }

  // 比较版本号或构建时间
  const needUpdate = 
    serverVersion.version !== localVersion.version ||
    serverVersion.buildTime !== localVersion.buildTime

  if (needUpdate) {
    console.log('🔄 检测到新版本：', {
      当前版本: localVersion.version,
      新版本: serverVersion.version,
      当前构建时间: localVersion.buildTimeStr,
      新构建时间: serverVersion.buildTimeStr
    })
  }

  return needUpdate
}

/**
 * 强制刷新页面
 */
export function forceReload() {
  // 清除缓存后刷新页面
  clearAppCache()
  
  // 强制从服务器重新加载
  window.location.href = window.location.href.split('?')[0] + '?t=' + Date.now()
}

/**
 * 启动版本检测定时器
 * @param {Function} onUpdateCallback 检测到更新时的回调函数
 */
export function startVersionCheck(onUpdateCallback) {
  // 立即执行一次检测
  checkVersionUpdate().then(needUpdate => {
    if (needUpdate && onUpdateCallback) {
      onUpdateCallback()
    }
  })

  // 定时检测
  const timer = setInterval(async () => {
    const needUpdate = await checkVersionUpdate()
    if (needUpdate && onUpdateCallback) {
      onUpdateCallback()
    }
  }, CHECK_INTERVAL)

  // 返回清除定时器的函数
  return () => clearInterval(timer)
}

/**
 * 显示版本信息（用于调试）
 */
export async function showVersionInfo() {
  const serverVersion = await fetchServerVersion()
  const localVersion = getLocalVersion()
  
  console.group('📦 应用版本信息')
  console.log('本地版本：', localVersion)
  console.log('服务器版本：', serverVersion)
  console.groupEnd()
  
  return {
    local: localVersion,
    server: serverVersion
  }
}


// WEBPACK FOOTER //
// ./src/utils/versionCheck.js